import filterStatics from '../../../console/_statics/filter.statics.json';

/**
 * The operator table, however the JSON import resolves.
 *
 * <p>This folder already imports `filter.statics.json` two different ways -- `FilterModal` as a
 * default, `filter.helpers` as a namespace that then reaches through `.default` -- and the two
 * disagree depending on the interop settings of whatever is compiling. Resolving both here is
 * cheaper than betting on one and finding out in a browser.
 */
const OPERATORS: Record<string, Record<string, { text?: string }>> =
  (filterStatics as any)?.operators ??
  (filterStatics as any)?.default?.operators ??
  {};

export interface FilterCondition {
  readonly column?: string | null;
  readonly operator?: string | null;
  readonly value?: string | readonly string[] | null;
}

export interface FilterColumnConfig {
  readonly text?: string;
  readonly type?: string;
}

export type FilterConfig = Readonly<Record<string, FilterColumnConfig>>;

const isBlank = (value: FilterCondition['value']): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === null || value === undefined || value === '';
};

/**
 * A row with all three parts chosen.
 *
 * <p>The same rule the save button is gated on, so the count the panel shows and the thing that
 * would actually be stored cannot disagree -- a half-filled row saying "2 conditions" beside a
 * disabled button is the confusing case.
 */
export const isCompleteCondition = (condition: FilterCondition): boolean =>
  Boolean(condition?.column) &&
  Boolean(condition?.operator) &&
  !isBlank(condition?.value);

/** Whatever was typed or picked, as one readable string. */
const describeValue = (value: FilterCondition['value']): string =>
  Array.isArray(value) ? value.join(', ') : String(value ?? '');

/**
 * One condition in the words the person chose it with -- "Claim Number contains a".
 *
 * <p>Column and operator are shown by their labels rather than their stored keys, because the
 * stored keys are `claimNumber` and `co`, and this line exists to let somebody confirm they are
 * saving the filter they think they are.
 */
export const describeCondition = (
  condition: FilterCondition,
  config: FilterConfig = {}
): string => {
  const column = condition?.column ?? '';
  const columnConfig = config?.[column];
  const columnLabel = columnConfig?.text ?? column;
  const operatorKey = condition?.operator ?? '';
  const operatorLabel =
    OPERATORS[columnConfig?.type ?? '']?.[operatorKey]?.text ?? operatorKey;

  return [columnLabel, operatorLabel, describeValue(condition?.value)]
    .filter(Boolean)
    .join(' ');
};

/** Every complete condition, in the order they were built. */
export const describeConditions = (
  query: readonly FilterCondition[] = [],
  config: FilterConfig = {}
): string =>
  (query ?? [])
    .filter(isCompleteCondition)
    .map((condition) => describeCondition(condition, config))
    .join(', ');

export const countConditions = (
  query: readonly FilterCondition[] = []
): number => (query ?? []).filter(isCompleteCondition).length;

/** "1 condition" / "3 conditions". */
export const conditionCountLabel = (count: number): string =>
  `${count} ${count === 1 ? 'condition' : 'conditions'}`;
